import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { v4 } from 'uuid';
import { ResourceType } from '@cotera/api';
import { toast } from '@cotera/client/app/components/ui';
import type {
  ApiErr,
  TenantedClient,
} from '@cotera/client/app/etc/api/typed-client';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import {
  resolveResourceNavigator,
  type ResourceNavigator,
} from '../resources/navigation';
import { DatasetsListPageModel } from './datasets-list-page.model';

type Params = {
  sourceQuery: string;
  rowKeyColumns: string[];
  cadence: string;
  name?: string;
};

/** Only the client surface this action actually calls. */
type CreateWarehouseDatasetActionClient = {
  resource: {
    dataset: Pick<TenantedClient['resource']['dataset'], 'createWarehouse'>;
  };
};

const createErrorMessage = (error: ApiErr): string =>
  error.err.message ?? 'Failed to create warehouse dataset.';

/**
 * Creates a warehouse dataset from a SELECT and opens it.
 *
 * The create request records the query and returns immediately at
 * `importing` — the seed runs in a saga — so this navigates to the dataset the
 * moment it exists and the page shows the same import progress a CSV upload
 * does. There is no client-side channel to seed, unlike CSV: the server owns
 * every phase from the first row, so the page reads it straight off the
 * persisted status and the broadcasts.
 */
export class CreateWarehouseDatasetAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Create warehouse dataset';
  description = 'Create a dataset seeded from a warehouse query';
  inputSchema = z.object({
    sourceQuery: z.string().min(1),
    rowKeyColumns: z.array(z.string().min(1)).min(1),
    cadence: z.string().min(1),
    name: z.string().optional(),
  });
  group = 'Dataset';
  icon = 'database';

  constructor(
    private readonly client: CreateWarehouseDatasetActionClient,
    private readonly fallbackNavigator: ResourceNavigator
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const sourceQuery = params.sourceQuery?.trim() ?? '';
    if (sourceQuery === '') {
      toast.error('Enter a SELECT statement.');
      return err([{ t: 'error', message: 'No query provided' }]);
    }

    const datasetId = v4();
    const created = await this.client.resource.dataset.createWarehouse({
      id: datasetId,
      sourceQuery,
      rowKeyColumns: params.rowKeyColumns,
      cadence: params.cadence,
      ...(params.name !== undefined && { name: params.name }),
    });
    if (created.isErr()) {
      const message = createErrorMessage(created.error);
      toast.error(message);
      return err([{ t: 'error', message }]);
    }

    // Open the dataset now: it exists at `importing`, and its page shows the
    // seed's progress off the persisted status and the server's broadcasts.
    context.getInScopeModelOfType(DatasetsListPageModel)?.refresh();
    resolveResourceNavigator(context, this.fallbackNavigator).open({
      resourceType: ResourceType.Dataset,
      resourceId: datasetId,
    });

    return ok({ resourceId: datasetId });
  }
}
